import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Activity,
  Calendar,
  DollarSign,
  Users,
  Eye,
  XCircle,
  ArrowUpRight,
  ArrowRight,
  MessageSquare,
  Clock,
  Target,
  TrendingUp,
  Network,
  Layers,
  Filter,
  Loader2,
  Download,
  Search,
  RefreshCw,
  X,
  Save,
  Undo2,
  Trash2,
} from 'lucide-react';


import AlertaCard, { SEVERIDAD_COLORS, formatMoney, formatDate } from './alertas/AlertaCard';


// Componente principal de alertas embebidas
const AlertasEmbebidas = ({ caso, onMovimientosFilter, onRefresh, onCasoUpdated, onHasUnsavedChanges }) => {
  const [expandedAlertId, setExpandedAlertId] = useState(null);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionForm, setActionForm] = useState({ comentario: '', asignar_a: '' });
  const [saving, setSaving] = useState(false);
  const [filterSeveridad, setFilterSeveridad] = useState('TODAS');
  
  // Estados para el modal de movimientos
  const [showMovimientosModal, setShowMovimientosModal] = useState(false);
  const [movimientosData, setMovimientosData] = useState([]);
  const [loadingMovimientos, setLoadingMovimientos] = useState(false);
  const [movimientosAlerta, setMovimientosAlerta] = useState(null);
  const [movimientosBusqueda, setMovimientosBusqueda] = useState('');
  const [loadingAllMovs, setLoadingAllMovs] = useState(false);
  const [allMovsLoaded, setAllMovsLoaded] = useState(false);
  
  // Estados para eliminación de alertas con guardado pendiente
  const [alertasEliminadas, setAlertasEliminadas] = useState(new Set());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Vista consolidada del cliente (todas las reincidencias / casos del mismo cliente)
  const navigate = useNavigate();
  const [consol, setConsol] = useState(null);
  const [vista, setVista] = useState('caso'); // 'caso' | 'consolidado'

  useEffect(() => {
    if (!caso?.id) return;
    let cancel = false;
    (async () => {
      try {
        const res = await api.get(`/cases/${caso.id}/alertas-consolidadas`);
        if (cancel) return;
        setConsol(res.data);
        if (res.data?.es_consolidado) setVista('consolidado');
      } catch { /* sin consolidación, sigue como caso simple */ }
    })();
    return () => { cancel = true; };
  }, [caso?.id]);

  // Obtener alertas del caso
  const alertas = caso?.alertas || [];
  const totalAlertas = caso?.total_alertas || alertas.length;
  
  // Alertas visibles (excluyendo las pendientes de eliminar para el conteo)
  const alertasVisibles = alertas.filter(a => !alertasEliminadas.has(a.id));

  // Filtrar alertas
  const alertasFiltradas = filterSeveridad === 'TODAS' 
    ? alertas 
    : alertas.filter(a => a.severidad === filterSeveridad);

  // Calcular métricas (solo de alertas no eliminadas)
  // Deduplicar: alertas del mismo tipo sobre los MISMOS movimientos no deben sumarse 2 veces.
  const _movSet = new Set();
  const _sigSet = new Set();
  let totalMonto = 0;
  alertasVisibles.forEach((a) => {
    const ids = (a.movimientos_ids || []).map((x) => String(x));
    ids.forEach((id) => _movSet.add(id));
    const sig = `${a.tipo || ''}|${[...ids].sort().join(',')}`;
    if (!_sigSet.has(sig)) {
      _sigSet.add(sig);
      totalMonto += (a.monto_total || 0);
    }
  });
  const totalMovimientos = _movSet.size > 0
    ? _movSet.size
    : alertasVisibles.reduce((sum, a) => sum + (a.cantidad_movimientos || 0), 0);
  const alertasCriticas = alertasVisibles.filter(a => a.severidad === 'CRITICA').length;
  const alertasAltas = alertasVisibles.filter(a => a.severidad === 'ALTA').length;

  // Manejar eliminación de alerta (marca como pendiente)
  const handleDeleteAlerta = useCallback((alerta) => {
    setAlertasEliminadas(prev => {
      const newSet = new Set(prev);
      newSet.add(alerta.id);
      return newSet;
    });
    setHasUnsavedChanges(true);
    toast.info(`Alerta "${alerta.tipo}" marcada para eliminar`, {
      description: 'Guarda los cambios para confirmar'
    });
  }, []);

  // Deshacer eliminación
  const handleUndoDelete = useCallback((alertaId) => {
    setAlertasEliminadas(prev => {
      const newSet = new Set(prev);
      newSet.delete(alertaId);
      return newSet;
    });
    if (alertasEliminadas.size <= 1) {
      setHasUnsavedChanges(false);
    }
  }, [alertasEliminadas.size]);

  // Deshacer todas las eliminaciones
  const handleUndoAll = useCallback(() => {
    setAlertasEliminadas(new Set());
    setHasUnsavedChanges(false);
    toast.success('Cambios descartados');
  }, []);

  // Guardar cambios (eliminar alertas permanentemente)
  const handleSaveChanges = async () => {
    if (alertasEliminadas.size === 0) return;
    
    setSaving(true);
    try {
      // Filtrar alertas que no están marcadas para eliminar
      const alertasRestantes = alertas.filter(a => !alertasEliminadas.has(a.id));
      
      // Actualizar el caso en el backend
      await api.put(`/cases/${caso.id}`, {
        alertas: alertasRestantes,
        total_alertas: alertasRestantes.length
      });
      
      // Notificar al componente padre para que actualice el caso
      if (onCasoUpdated) {
        onCasoUpdated({
          ...caso,
          alertas: alertasRestantes,
          total_alertas: alertasRestantes.length
        });
      }
      
      // Limpiar estados
      setAlertasEliminadas(new Set());
      setHasUnsavedChanges(false);
      
      toast.success(`${alertasEliminadas.size} alerta(s) eliminada(s) correctamente`);
    } catch (error) {
      console.error('Error guardando cambios:', error);
      toast.error('Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  // Notificar al padre cuando hay cambios no guardados
  useEffect(() => {
    if (onHasUnsavedChanges) {
      onHasUnsavedChanges(hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, onHasUnsavedChanges]);

  const handleAction = (alerta, type) => {
    setSelectedAlert(alerta);
    setActionType(type);
    setActionForm({ comentario: '', asignar_a: '' });
    setShowActionDialog(true);
  };

  const handleVerMovimientos = async (alerta) => {
    // Obtener info para filtrar movimientos
    const cuentaId = alerta.datos_clave?.cvu_principal || caso?.cuit || caso?.cliente_info?.cvu_principal;
    const movIds = alerta.movimientos_ids || [];
    const cantDetectada = alerta.cantidad_movimientos || alerta.cantidad_transacciones || 0;
    
    // Si hay más IDs almacenados que detectados, solo cargar los detectados (los primeros N)
    const idsToLoad = (cantDetectada > 0 && movIds.length > cantDetectada)
      ? movIds.slice(0, cantDetectada)
      : movIds;
    
    // Guardar la alerta seleccionada y abrir el modal
    setMovimientosAlerta(alerta);
    setShowMovimientosModal(true);
    setLoadingMovimientos(true);
    setMovimientosData([]);
    setMovimientosBusqueda('');
    setAllMovsLoaded(false);
    
    try {
      // Cargar movimientos desde el backend
      let response;
      
      if (idsToLoad.length > 0) {
        // Si hay IDs específicos, buscar por IDs
        response = await api.post('/movimientos/by-ids', { 
          ids: idsToLoad.slice(0, 500)
        });
      } else if (cuentaId) {
        // Si no hay IDs, buscar por cuenta del cliente
        response = await api.get(`/movimientos?cvu=${cuentaId}&limit=500`);
      } else {
        // Buscar por CUIT del caso
        response = await api.get(`/movimientos?cuit=${caso?.cuit || ''}&limit=500`);
      }
      
      const movimientos = response.data?.movimientos || response.data || [];
      setMovimientosData(Array.isArray(movimientos) ? movimientos : []);
      
      if (movimientos.length === 0) {
        toast.info('No se encontraron movimientos para esta alerta');
      }
    } catch (error) {
      console.error('Error cargando movimientos:', error);
      toast.error('Error al cargar los movimientos');
      
      // Intentar obtener movimientos del caso si están disponibles
      if (caso?.movimientos_ids?.length > 0) {
        try {
          const fallbackResponse = await api.post('/movimientos/by-ids', {
            ids: caso.movimientos_ids.slice(0, 200)
          });
          setMovimientosData(fallbackResponse.data || []);
        } catch (e) {
          console.error('Error en fallback:', e);
        }
      }
    } finally {
      setLoadingMovimientos(false);
    }
  };

  const handleCargarTodas = async () => {
    if (!movimientosAlerta) return;
    const cvu = movimientosAlerta.datos_clave?.cvu_principal || caso?.cuit || caso?.cvu_principal || '';
    const per = movimientosAlerta.periodo || {};
    if (!cvu) {
      toast.error('No se puede determinar el CVU para buscar');
      return;
    }
    setLoadingAllMovs(true);
    try {
      const res = await api.post('/movimientos/by-period', {
        cvu,
        fecha_desde: per.desde || movimientosAlerta.fecha_primera || null,
        fecha_hasta: per.hasta || movimientosAlerta.fecha_ultima || null,
      });
      const movs = res.data?.movimientos || [];
      if (movs.length > 0) {
        setMovimientosData(movs);
        setAllMovsLoaded(true);

        // Persistir los IDs nuevos en la alerta del caso
        const newIds = movs.map(m => m.id_waya).filter(Boolean);
        if (newIds.length > 0 && caso?.id && movimientosAlerta?.id) {
          try {
            await api.post(`/cases/${caso.id}/alertas/${movimientosAlerta.id}/update-movimientos`, {
              movimientos_ids: newIds.slice(0, 500),
            });
          } catch { /* silently fail - data is shown anyway */ }
        }

        toast.success(`${movs.length} movimientos cargados y almacenados`);
      } else {
        toast.info('No se encontraron movimientos adicionales');
      }
    } catch {
      toast.error('Error al cargar todas las operaciones');
    } finally {
      setLoadingAllMovs(false);
    }
  };

  const handleSubmitAction = async () => {
    if (!selectedAlert || !actionType) return;
    
    setSaving(true);
    try {
      // Usar el endpoint dedicado que registra en timeline
      const res = await api.post(`/cases/${caso.id}/alertas/${selectedAlert.id}/notas`, {
        alerta_id: selectedAlert.id,
        comentario: actionForm.comentario,
        tipo: actionType,
      });

      // Actualizar caso con la respuesta del backend
      if (onCasoUpdated && res.data) {
        onCasoUpdated(res.data);
      }

      toast.success(
        actionType === 'ESCALAR' ? 'Alerta escalada correctamente' :
        actionType === 'FALSO_POSITIVO' ? 'Marcada como falso positivo' :
        'Nota agregada correctamente'
      );
      
      setShowActionDialog(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating alert:', error);
      toast.error('Error al actualizar la alerta');
    } finally {
      setSaving(false);
    }
  };

  if (alertas.length === 0 && !(consol?.total_alertas > 0)) {
    return (
      <div className="text-center py-12 text-slate-400">
        <AlertTriangle size={48} className="mx-auto mb-3 opacity-50" />
        <p className="text-lg font-medium">No hay alertas detectadas</p>
        <p className="text-sm">Este caso no tiene alertas embebidas del motor de monitores</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Panel consolidado del cliente (reincidencias / todos los casos del mismo cliente) */}
      {consol?.es_consolidado && (
        <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-indigo-50 p-4" data-testid="consolidado-panel">
          <div className="flex items-center gap-2 mb-3">
            <Layers size={18} className="text-violet-600" />
            <h3 className="text-sm font-bold text-violet-900">
              Visión consolidada del cliente — {consol.num_casos} casos
            </h3>
            {consol.patron_acumulado && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-100 text-red-700 border border-red-300" data-testid="patron-acumulado-badge">
                <TrendingUp size={12} /> Patrón acumulado significativo
              </span>
            )}
          </div>
          <p className="text-xs text-violet-700/80 mb-3">
            Sumando todas las reincidencias del cliente. Una alerta aislada puede no decir nada; en conjunto el patrón se vuelve relevante.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-white/70 rounded-lg p-2.5 border border-violet-100" data-testid="consol-kpi-casos">
              <div className="text-[11px] text-slate-500">Casos del cliente</div>
              <div className="text-2xl font-bold text-violet-800">{consol.num_casos}</div>
            </div>
            <div className="bg-white/70 rounded-lg p-2.5 border border-violet-100" data-testid="consol-kpi-alertas">
              <div className="text-[11px] text-slate-500">Alertas totales</div>
              <div className="text-2xl font-bold text-slate-800">{consol.total_alertas}</div>
            </div>
            <div className="bg-white/70 rounded-lg p-2.5 border border-violet-100">
              <div className="text-[11px] text-slate-500">Críticas + Altas</div>
              <div className="text-2xl font-bold text-red-700">{(consol.sev_count?.CRITICA || 0) + (consol.sev_count?.ALTA || 0)}</div>
            </div>
            <div className="bg-white/70 rounded-lg p-2.5 border border-violet-100">
              <div className="text-[11px] text-slate-500">Monto acumulado</div>
              <div className="text-lg font-bold text-green-700">{formatMoney(consol.total_monto || 0)}</div>
            </div>
            <div className="bg-white/70 rounded-lg p-2.5 border border-violet-100">
              <div className="text-[11px] text-slate-500">Movimientos</div>
              <div className="text-2xl font-bold text-blue-700">{consol.total_movimientos}</div>
            </div>
          </div>

          {/* Patrones recurrentes (mismo monitor en varias reincidencias) */}
          {consol.patrones_recurrentes?.length > 0 && (
            <div className="mt-3" data-testid="patrones-recurrentes">
              <div className="text-[11px] uppercase font-semibold text-violet-700 mb-1.5 flex items-center gap-1">
                <RefreshCw size={12} /> Patrones recurrentes
              </div>
              <div className="flex flex-wrap gap-2">
                {consol.patrones_recurrentes.map((p, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-white border border-violet-300 text-violet-800" data-testid={`patron-${i}`}>
                    <Target size={11} /> {p.tipo}
                    <span className="ml-1 px-1.5 rounded-full bg-violet-600 text-white font-bold">{p.casos}×</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Mini línea de tiempo de montos por reincidencia */}
          {consol.timeline_montos?.length > 1 && (
            <div className="mt-3" data-testid="timeline-montos">
              <div className="text-[11px] uppercase font-semibold text-violet-700 mb-1.5">Evolución por episodio</div>
              <div className="flex items-end gap-1.5 h-16">
                {(() => {
                  const maxM = Math.max(...consol.timeline_montos.map(t => t.monto || 0), 1);
                  return consol.timeline_montos.map((t, i) => {
                    const h = Math.max(6, Math.round(((t.monto || 0) / maxM) * 56));
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center justify-end group" title={`${formatDate(t.fecha)} · ${formatMoney(t.monto || 0)} · ${t.alertas_count} alerta(s)`}>
                        <div className={`w-full rounded-t ${t.es_actual ? 'bg-violet-600' : 'bg-violet-300'} group-hover:bg-violet-500 transition-colors`} style={{ height: `${h}px` }} />
                        <span className="text-[8px] text-slate-400 mt-0.5 truncate w-full text-center">{t.fecha ? new Date(t.fecha).toLocaleDateString('es-AR', { month: 'short', day: '2-digit' }) : ''}</span>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {/* Toggle de vista */}
          <div className="flex items-center gap-2 mt-4">
            <Button size="sm" variant={vista === 'consolidado' ? 'default' : 'outline'} className={`h-7 text-xs ${vista === 'consolidado' ? 'bg-violet-600 hover:bg-violet-700' : ''}`} onClick={() => setVista('consolidado')} data-testid="vista-consolidado-btn">
              Todas las reincidencias
            </Button>
            <Button size="sm" variant={vista === 'caso' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setVista('caso')} data-testid="vista-caso-btn">
              Solo este caso
            </Button>
          </div>
        </div>
      )}

      {/* Dashboard de métricas */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="p-3 bg-gradient-to-br from-slate-50 to-slate-100">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={16} className="text-amber-500" />
            <span className="text-xs text-slate-500">Total Alertas</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{totalAlertas}</p>
        </Card>
        
        <Card className="p-3 bg-gradient-to-br from-red-50 to-red-100">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-xs text-red-600">Críticas</span>
          </div>
          <p className="text-2xl font-bold text-red-700">{alertasCriticas}</p>
        </Card>
        
        <Card className="p-3 bg-gradient-to-br from-orange-50 to-orange-100">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2 h-2 rounded-full bg-orange-500" />
            <span className="text-xs text-orange-600">Altas</span>
          </div>
          <p className="text-2xl font-bold text-orange-700">{alertasAltas}</p>
        </Card>
        
        <Card className="p-3 bg-gradient-to-br from-green-50 to-green-100">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign size={16} className="text-green-500" />
            <span className="text-xs text-green-600">Monto Total</span>
          </div>
          <p className="text-lg font-bold text-green-700">{formatMoney(totalMonto)}</p>
        </Card>
        
        <Card className="p-3 bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="flex items-center gap-2 mb-1">
            <Activity size={16} className="text-blue-500" />
            <span className="text-xs text-blue-600">Movimientos</span>
          </div>
          <p className="text-2xl font-bold text-blue-700">{totalMovimientos}</p>
        </Card>
      </div>

      {/* Filtro de severidad */}
      <div className="flex items-center gap-2">
        <Filter size={16} className="text-slate-400" />
        <span className="text-sm text-slate-600">Filtrar:</span>
        <div className="flex gap-1">
          {['TODAS', 'CRITICA', 'ALTA', 'MEDIA', 'BAJA'].map(sev => (
            <Button
              key={sev}
              variant={filterSeveridad === sev ? 'default' : 'outline'}
              size="sm"
              className={`h-7 text-xs ${
                filterSeveridad === sev 
                  ? sev === 'CRITICA' ? 'bg-red-600' :
                    sev === 'ALTA' ? 'bg-orange-600' :
                    sev === 'MEDIA' ? 'bg-yellow-600' :
                    sev === 'BAJA' ? 'bg-green-600' : ''
                  : ''
              }`}
              onClick={() => setFilterSeveridad(sev)}
            >
              {sev === 'TODAS' ? 'Todas' : sev}
              {sev !== 'TODAS' && (
                <span className="ml-1 opacity-70">
                  ({alertas.filter(a => a.severidad === sev).length})
                </span>
              )}
            </Button>
          ))}
        </div>
      </div>

      {/* Barra de acciones de cambios pendientes */}
      {hasUnsavedChanges && (
        <div className="sticky top-0 z-20 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg shadow-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-600" />
            <span className="text-sm font-medium text-amber-800">
              {alertasEliminadas.size} alerta(s) pendiente(s) de eliminar
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleUndoAll}
              className="text-slate-600 border-slate-300"
            >
              <Undo2 size={14} className="mr-1" />
              Deshacer todo
            </Button>
            <Button
              size="sm"
              onClick={handleSaveChanges}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {saving ? (
                <>
                  <Loader2 size={14} className="mr-1 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={14} className="mr-1" />
                  Guardar cambios
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Lista de alertas */}
      {vista === 'consolidado' && consol?.es_consolidado ? (
        <div className="space-y-5" data-testid="alertas-consolidadas-list">
          {consol.grupos.map((grupo) => {
            const filt = filterSeveridad === 'TODAS'
              ? grupo.alertas
              : grupo.alertas.filter(a => a.severidad === filterSeveridad);
            if (!filt || filt.length === 0) return null;
            return (
              <div key={grupo.case_id} data-testid={`alertas-grupo-${grupo.case_id}`}>
                <div className={`flex items-center justify-between gap-2 mb-2 px-3 py-2 rounded-lg border ${grupo.es_actual ? 'bg-violet-50 border-violet-200' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Layers size={14} className={grupo.es_actual ? 'text-violet-600' : 'text-slate-400'} />
                    <span className={`text-sm font-semibold ${grupo.es_actual ? 'text-violet-900' : 'text-slate-700'}`}>
                      {grupo.es_actual ? 'Este caso' : 'Caso anterior'}
                    </span>
                    <span className="text-xs text-slate-500">· {formatDate(grupo.fecha)}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-600 font-medium">{grupo.estado}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-600">{filt.length} alerta(s)</span>
                  </div>
                  {!grupo.es_actual && (
                    <Button size="sm" variant="outline" className="h-7 text-xs flex-shrink-0" onClick={() => navigate(`/m/gestion-de-casos/caso/${grupo.case_id}`)} data-testid={`abrir-caso-${grupo.case_id}`}>
                      <ArrowUpRight size={12} className="mr-1" /> Abrir
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {filt.map((alerta, idx) => (
                    <AlertaCard
                      key={alerta.id || `${grupo.case_id}-${idx}`}
                      alerta={alerta}
                      onAction={grupo.es_actual ? handleAction : () => navigate(`/m/gestion-de-casos/caso/${grupo.case_id}`)}
                      onVerMovimientos={handleVerMovimientos}
                      onDelete={grupo.es_actual ? handleDeleteAlerta : undefined}
                      isPendingDelete={grupo.es_actual && alertasEliminadas.has(alerta.id)}
                      isExpanded={expandedAlertId === (alerta.id || `${grupo.case_id}-${idx}`)}
                      onToggleExpand={() => setExpandedAlertId(
                        expandedAlertId === (alerta.id || `${grupo.case_id}-${idx}`) ? null : (alerta.id || `${grupo.case_id}-${idx}`)
                      )}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {alertasFiltradas.map((alerta, idx) => (
            <AlertaCard
              key={alerta.id || idx}
              alerta={alerta}
              onAction={handleAction}
              onVerMovimientos={handleVerMovimientos}
              onDelete={handleDeleteAlerta}
              isPendingDelete={alertasEliminadas.has(alerta.id)}
              isExpanded={expandedAlertId === (alerta.id || idx)}
              onToggleExpand={() => setExpandedAlertId(
                expandedAlertId === (alerta.id || idx) ? null : (alerta.id || idx)
              )}
            />
          ))}
        </div>
      )}

      {vista !== 'consolidado' && alertasFiltradas.length === 0 && (
        <div className="text-center py-8 text-slate-400">
          <p>No hay alertas con severidad "{filterSeveridad}"</p>
        </div>
      )}

      {/* Dialog de acción */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'ESCALAR' && 'Escalar Alerta'}
              {actionType === 'FALSO_POSITIVO' && 'Marcar como Falso Positivo'}
              {actionType === 'AGREGAR_NOTA' && 'Agregar Nota'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedAlert && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="font-medium text-slate-800">{selectedAlert.tipo || 'Alerta'}</p>
                <p className="text-slate-500 text-xs mt-1">{selectedAlert.descripcion?.slice(0, 100)}</p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                {actionType === 'ESCALAR' ? 'Motivo de escalamiento' :
                 actionType === 'FALSO_POSITIVO' ? 'Justificación' :
                 'Nota'}
              </label>
              <Textarea
                value={actionForm.comentario}
                onChange={(e) => setActionForm({ ...actionForm, comentario: e.target.value })}
                placeholder={
                  actionType === 'ESCALAR' ? 'Describa por qué se escala esta alerta...' :
                  actionType === 'FALSO_POSITIVO' ? 'Explique por qué es un falso positivo...' :
                  'Escriba su nota aquí...'
                }
                rows={3}
              />
            </div>

            {actionType === 'ESCALAR' && (
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  Asignar a (opcional)
                </label>
                <Input
                  value={actionForm.asignar_a}
                  onChange={(e) => setActionForm({ ...actionForm, asignar_a: e.target.value })}
                  placeholder="Email del usuario..."
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmitAction}
              disabled={saving || !actionForm.comentario.trim()}
              className={`text-white ${
                actionType === 'ESCALAR' ? 'bg-orange-600 hover:bg-orange-700' :
                actionType === 'FALSO_POSITIVO' ? 'bg-green-600 hover:bg-green-700' :
                'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {saving ? 'Guardando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Movimientos */}
      <Dialog open={showMovimientosModal} onOpenChange={setShowMovimientosModal}>
        <DialogContent className="bg-white max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Activity size={20} className="text-blue-600" />
              Movimientos - {movimientosAlerta?.tipo || 'Alerta'}
            </DialogTitle>
            {movimientosAlerta && (
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge className={SEVERIDAD_COLORS[movimientosAlerta.severidad]?.bg + ' ' + SEVERIDAD_COLORS[movimientosAlerta.severidad]?.text}>
                  {movimientosAlerta.severidad}
                </Badge>
                <span className="text-sm text-slate-500">
                  Cliente: {caso?.cuit || caso?.cliente_info?.cuit || 'N/A'}
                </span>
                {movimientosAlerta.periodo?.desde && (
                  <span className="text-sm text-slate-500">
                    | Período: {formatDate(movimientosAlerta.periodo.desde)?.split(',')[0]} - {formatDate(movimientosAlerta.periodo.hasta)?.split(',')[0]}
                  </span>
                )}
              </div>
            )}
          </DialogHeader>
          
          {/* Barra de búsqueda y acciones */}
          <div className="flex items-center gap-2 py-3 border-b">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar por CBU, monto, concepto..."
                value={movimientosBusqueda}
                onChange={(e) => setMovimientosBusqueda(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleVerMovimientos(movimientosAlerta)}
              disabled={loadingMovimientos}
            >
              <RefreshCw size={14} className={loadingMovimientos ? 'animate-spin' : ''} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // Exportar a CSV con estructura corregida
                if (movimientosData.length === 0) return;
                const headers = ['Fecha', 'Monto', 'Tipo', 'Cliente CVU', 'Contraparte', 'Concepto', 'Estado'];
                const rows = movimientosData.map(m => {
                  const esIngreso = m.movimiento === 'INGRESO' || 
                                   (m.cbu_cvu_destino && m.cbu_cvu_destino === m.cvu_mov);
                  const contraparte = esIngreso ? (m.cbu_cvu_origen || '') : (m.cbu_cvu_destino || '');
                  return [
                    m.fecha_acreditacion || m.fecha || '',
                    m.monto || '',
                    m.movimiento || (esIngreso ? 'INGRESO' : 'EGRESO'),
                    m.cvu_mov || '',
                    contraparte,
                    m.concepto || '',
                    m.estado_pago || ''
                  ];
                });
                const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `movimientos_${caso?.cuit || 'export'}_${new Date().toISOString().split('T')[0]}.csv`;
                a.click();
                toast.success('Archivo CSV descargado');
              }}
            >
              <Download size={14} className="mr-1" />
              Exportar CSV
            </Button>
          </div>

          {/* Contenido del modal */}
          <div className="flex-1 overflow-auto">
            {loadingMovimientos ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 size={40} className="animate-spin text-blue-500 mb-4" />
                <p className="text-slate-500">Cargando movimientos...</p>
              </div>
            ) : movimientosData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                <Activity size={48} className="mb-4 opacity-50" />
                <p className="text-lg font-medium">No se encontraron movimientos</p>
                <p className="text-sm">Los movimientos pueden no estar disponibles para esta alerta</p>
              </div>
            ) : (
              <>
                {/* Banner de datos parciales */}
                {(() => {
                  const cantReal = movimientosAlerta?.cantidad_movimientos || movimientosAlerta?.cantidad_transacciones || 0;
                  const cantStored = (movimientosAlerta?.movimientos_ids || []).length;
                  const montoReal = parseFloat(movimientosAlerta?.monto_total) || 0;
                  const hayParcial = cantReal > cantStored && cantStored > 0 && !allMovsLoaded;

                  if (!hayParcial) return null;
                  return (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="partial-data-banner">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-amber-800">
                            El monitor detectó {cantReal} operaciones{montoReal > 0 ? ` por ${formatMoney(montoReal)}` : ''}, pero solo se almacenaron {cantStored} IDs de movimientos.
                          </p>
                          <p className="text-xs text-amber-600 mt-1">
                            Los datos de abajo corresponden a los {cantStored} movimientos almacenados. Podés cargar todas las operaciones del período para ver el set completo.
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="shrink-0 bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200"
                          onClick={handleCargarTodas}
                          disabled={loadingAllMovs}
                          data-testid="load-all-movs-btn"
                        >
                          {loadingAllMovs ? <Loader2 size={14} className="animate-spin mr-1" /> : <Activity size={14} className="mr-1" />}
                          Cargar todas ({cantReal})
                        </Button>
                      </div>
                    </div>
                  );
                })()}

                {/* Visualización especial para Flujo Circular */}
                {(movimientosAlerta?.tipo?.toLowerCase().includes('circular') || 
                  movimientosAlerta?.monitor_nombre?.toLowerCase().includes('circular') ||
                  movimientosAlerta?.titulo_detalle?.toLowerCase().includes('circular')) && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                      <RefreshCw size={18} className="text-purple-600" />
                      Análisis de Flujo Circular Detectado
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Resumen del flujo */}
                      <div className="space-y-3">
                        <p className="text-sm text-purple-700">
                          Se detectó un patrón de movimientos donde el dinero circula entre cuentas relacionadas.
                        </p>
                        {(() => {
                          // Analizar los movimientos para extraer el flujo
                          const cuentasInvolucradas = new Set();
                          const flujos = [];
                          
                          movimientosData.forEach(mov => {
                            if (mov.cbu_cvu_origen) cuentasInvolucradas.add(mov.cbu_cvu_origen);
                            if (mov.cbu_cvu_destino) cuentasInvolucradas.add(mov.cbu_cvu_destino);
                            if (mov.cbu_cvu_origen && mov.cbu_cvu_destino) {
                              flujos.push({
                                origen: mov.cbu_cvu_origen.slice(-6),
                                destino: mov.cbu_cvu_destino.slice(-6),
                                monto: mov.monto,
                                fecha: mov.fecha_acreditacion || mov.fecha
                              });
                            }
                          });
                          
                          return (
                            <>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-purple-800">Cuentas involucradas:</span>
                                <span className="text-purple-600">{cuentasInvolucradas.size}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-purple-800">Transferencias:</span>
                                <span className="text-purple-600">{flujos.length}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-purple-800">Monto total:</span>
                                <span className="text-purple-600 font-mono">
                                  ${movimientosData.reduce((sum, m) => sum + (parseFloat(m.monto) || 0), 0).toLocaleString('es-AR', {minimumFractionDigits: 2})}
                                </span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      
                      {/* Diagrama visual del flujo */}
                      <div className="bg-white rounded-lg p-4 border border-purple-200">
                        <p className="text-xs text-purple-600 mb-2 font-medium">Flujo de transferencias:</p>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {movimientosData.slice(0, 10).map((mov, idx) => {
                            const esIngreso = mov.movimiento === 'INGRESO' || 
                                             (mov.cbu_cvu_destino && mov.cbu_cvu_destino === mov.cvu_mov);
                            const origen = (mov.cbu_cvu_origen || 'Origen').slice(-8);
                            const destino = (mov.cbu_cvu_destino || 'Destino').slice(-8);
                            
                            return (
                              <div key={idx} className="flex items-center gap-2 text-xs">
                                <span className={`px-2 py-1 rounded font-mono ${esIngreso ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                  ...{origen}
                                </span>
                                <ArrowRight size={14} className="text-purple-500" />
                                <span className={`px-2 py-1 rounded font-mono ${esIngreso ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                                  ...{destino}
                                </span>
                                <span className="text-slate-500">
                                  ${parseFloat(mov.monto || 0).toLocaleString('es-AR')}
                                </span>
                              </div>
                            );
                          })}
                          {movimientosData.length > 10 && (
                            <p className="text-xs text-purple-500 italic">
                              + {movimientosData.length - 10} transferencias más...
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="text-sm text-slate-500 py-2">
                  {(() => {
                    const filteredCount = movimientosData.filter(m => {
                      if (!movimientosBusqueda) return true;
                      const search = movimientosBusqueda.toLowerCase();
                      return (
                        (m.cvu_mov || '').toLowerCase().includes(search) ||
                        (m.cbu_cvu_destino || '').toLowerCase().includes(search) ||
                        (m.concepto || '').toLowerCase().includes(search) ||
                        String(m.monto || '').includes(search)
                      );
                    }).length;
                    const cantDetectada = movimientosAlerta?.cantidad_movimientos || movimientosAlerta?.cantidad_transacciones || 0;
                    const total = movimientosData.length;
                    if (cantDetectada > 0 && cantDetectada !== total) {
                      return `Mostrando ${filteredCount} de ${total} movimientos (${cantDetectada} detectados por el monitor)`;
                    }
                    return `Mostrando ${filteredCount} de ${total} movimientos`;
                  })()}
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Fecha</TableHead>
                      <TableHead className="font-semibold text-right">Monto</TableHead>
                      <TableHead className="font-semibold">Tipo</TableHead>
                      <TableHead className="font-semibold">Cliente (CVU)</TableHead>
                      <TableHead className="font-semibold">Contraparte</TableHead>
                      <TableHead className="font-semibold">Concepto</TableHead>
                      <TableHead className="font-semibold">Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {movimientosData
                      .filter(m => {
                        if (!movimientosBusqueda) return true;
                        const search = movimientosBusqueda.toLowerCase();
                        return (
                          (m.cvu_mov || '').toLowerCase().includes(search) ||
                          (m.cbu_cvu_destino || '').toLowerCase().includes(search) ||
                          (m.cbu_cvu_origen || '').toLowerCase().includes(search) ||
                          (m.concepto || '').toLowerCase().includes(search) ||
                          String(m.monto || '').includes(search)
                        );
                      })
                      .slice(0, 200) // Mostrar máximo 200 filas
                      .map((mov, idx) => {
                        // cvu_mov es siempre el CVU del cliente
                        // Si es INGRESO: cbu_cvu_destino == cvu_mov, contraparte es cbu_cvu_origen
                        // Si es EGRESO: cbu_cvu_origen == cvu_mov, contraparte es cbu_cvu_destino
                        const esIngreso = mov.movimiento === 'INGRESO' || 
                                         (mov.cbu_cvu_destino && mov.cbu_cvu_destino === mov.cvu_mov);
                        const contraparte = esIngreso 
                          ? (mov.cbu_cvu_origen || '-')  // De donde viene el dinero
                          : (mov.cbu_cvu_destino || '-'); // A donde va el dinero
                        
                        return (
                          <TableRow key={mov.id_waya || mov.id || idx} className="hover:bg-slate-50">
                            <TableCell className="text-sm">
                              {mov.fecha_acreditacion || mov.fecha 
                                ? new Date(mov.fecha_acreditacion || mov.fecha).toLocaleDateString('es-AR', {
                                    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                  })
                                : 'N/A'}
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold">
                              <span className={esIngreso ? 'text-green-600' : 'text-red-600'}>
                                {esIngreso ? '+' : '-'}{formatMoney(Math.abs(mov.monto))}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant="outline"
                                className={esIngreso 
                                  ? 'bg-green-50 text-green-700 border-green-200' 
                                  : 'bg-red-50 text-red-700 border-red-200'
                                }
                              >
                                {mov.movimiento || (esIngreso ? 'INGRESO' : 'EGRESO')}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {mov.cvu_mov || '-'}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {contraparte}
                            </TableCell>
                            <TableCell className="text-sm max-w-[200px] truncate" title={mov.concepto}>
                              {mov.concepto || mov.descripcion || '-'}
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant="outline"
                                className={
                                  mov.estado_pago === 'ACREDITADO' || mov.estado_pago === 'APROBADO' 
                                    ? 'bg-green-50 text-green-700 border-green-200' 
                                    : mov.estado_pago === 'PENDIENTE'
                                    ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                    : 'bg-slate-50 text-slate-700 border-slate-200'
                                }
                              >
                                {mov.estado_pago || mov.estado || 'N/A'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
                
                {movimientosData.length > 200 && (
                  <div className="text-center py-3 text-sm text-slate-500 border-t">
                    Se muestran los primeros 200 movimientos de {movimientosData.length} totales
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <div className="flex items-center justify-between w-full">
              <div className="text-sm text-slate-500 space-y-0.5">
                {movimientosData.length > 0 && (() => {
                  const montoMostrado = movimientosData.reduce((sum, m) => sum + (m.monto || 0), 0);
                  const cantMostrada = movimientosData.length;
                  const cantDetectada = movimientosAlerta?.cantidad_movimientos || movimientosAlerta?.cantidad_transacciones || 0;
                  const montoDetectado = parseFloat(movimientosAlerta?.monto_total) || 0;

                  // Caso normal: coinciden o no hay info del monitor
                  if (cantDetectada <= 0 || cantDetectada === cantMostrada) {
                    return (
                      <span className="block">
                        Total: {formatMoney(montoMostrado)} en {cantMostrada} movimientos
                      </span>
                    );
                  }

                  // Caso: stored > detected (se cargó todo el período)
                  if (cantMostrada > cantDetectada) {
                    return (
                      <>
                        <span className="block">
                          {cantMostrada} movimientos del período — {formatMoney(montoMostrado)}
                        </span>
                        <span className="block text-xs text-blue-600">
                          El monitor detectó {cantDetectada} operaciones{montoDetectado > 0 ? ` por ${formatMoney(montoDetectado)}` : ''} como sospechosas
                        </span>
                      </>
                    );
                  }

                  // Caso: stored < detected (datos parciales)
                  return (
                    <>
                      <span className="block">
                        Mostrando: {formatMoney(montoMostrado)} en {cantMostrada} movimientos
                      </span>
                      <span className="block text-xs text-amber-600">
                        Monitor detectó {cantDetectada} operaciones{montoDetectado > 0 ? ` por ${formatMoney(montoDetectado)}` : ''}
                      </span>
                    </>
                  );
                })()}
              </div>
              <Button variant="outline" onClick={() => setShowMovimientosModal(false)}>
                Cerrar
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AlertasEmbebidas;
